import type { PageHelp } from '../../help/types';

/** Page guide for `/packages/:code/keys` — the editor on a package's own keys (spec §9.3, §9.5). */
export const packageKeysHelp: PageHelp = {
  title: 'Package keys',
  intro:
    'The editor for one package: the keys it owns, the source locale plus up to six locale columns, and inline editing that saves as you go. Every subscribing project exports these keys as its own, so a value written here reaches all of their bundles at their next publish.',
  sections: ['Getting around', 'Finding a key', 'Editing', 'Working on a selection'],
  contexts: {
    selection: 'Tick the checkbox at the start of one or more rows.',
  },
  tips: [
    'This is where a package grows: a new key needs one of the package’s prefixes, and a project’s Keys page refuses that prefix because the package owns it.',
    'Here your role on the package decides, not a role in some project. Owners and translators of the package edit values directly; owners and developers create, rename, tag and delete keys; administrators may do everything. The same edit made from a project’s Keys page becomes a suggestion unless you are the package owner.',
    'A package has no Export button: its keys reach files inside the bundles of the subscribing projects, or as the package’s own files from Releases.',
  ],
  steps: [
    {
      selector: '[data-help="keys-new"]',
      title: 'Create a package key',
      body: 'Opens the New key dialog for a name, an optional description and the source text. The name has to start with one of this package’s prefixes (see package settings); any other prefix is refused. The server accepts it from the owner or developer role on the package, and from administrators.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Paketschlüssel anlegen',
          body: 'Öffnet den Dialog „New key“ für Namen, optionale Beschreibung und Quelltext. Der Name muss mit einem der Präfixe dieses Pakets beginnen (siehe Paketeinstellungen); jedes andere Präfix wird abgelehnt. Der Server nimmt ihn von den Rollen Owner oder Developer im Paket sowie von Administratoren an.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje ključa paketa',
          body: 'Otvara dijalog „New key“ za ime, opcioni opis i izvorni tekst. Ime mora da počinje jednim od prefiksa ovog paketa (vidi podešavanja paketa); svaki drugi prefiks biva odbijen. Server ga prihvata od uloga owner ili developer na paketu i od administratora.',
        },
      },
    },
    {
      selector: '[data-help="keys-save"]',
      title: 'Save what is still pending',
      body: 'A cell saves itself when you leave it, so this button only counts what has not reached the server yet. It sends all of them in one batch, like Ctrl+S; if somebody saved one of those cells first, nothing is written and a dialog lets you keep yours or take theirs.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Ausstehendes speichern',
          body: 'Eine Zelle speichert sich beim Verlassen selbst; diese Schaltfläche zählt daher nur, was den Server noch nicht erreicht hat. Sie sendet alles in einem Durchgang, wie Strg+S; hat jemand eine dieser Zellen zuerst gespeichert, wird nichts geschrieben, und ein Dialog lässt Sie Ihre oder die andere Fassung behalten.',
        },
        'sr-Latn-RS': {
          title: 'Čuvanje onoga što čeka',
          body: 'Ćelija se sama sačuva kada je napustite, pa ovo dugme broji samo ono što još nije stiglo na server. Šalje sve odjednom, kao Ctrl+S; ako je neko pre vas sačuvao neku od tih ćelija, ništa se ne upisuje, a dijalog vam nudi da zadržite svoju ili preuzmete njegovu verziju.',
        },
      },
    },
    {
      selector: '[data-help="keys-ai-translate-all"]',
      title: 'Pre-translate with AI',
      body: 'Switched off for now: the button stays greyed out until AI translation is available. Opens the AI translate dialog for every package key that has a source value, or only for the ticked rows. The package description from its settings goes to the AI as context. The job fills empty cells as machine translations and never overwrites draft or reviewed text.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Mit KI vorübersetzen',
          body: 'Derzeit abgeschaltet: Die Schaltfläche bleibt ausgegraut, bis die KI-Übersetzung verfügbar ist. Öffnet den Dialog „AI translate“ für jeden Paketschlüssel mit Quellwert oder nur für die angehakten Zeilen. Die Paketbeschreibung aus den Einstellungen geht als Kontext an die KI. Der Auftrag füllt leere Zellen als maschinelle Übersetzung und überschreibt nie Texte im Status draft oder reviewed.',
        },
        'sr-Latn-RS': {
          title: 'AI predprevod',
          body: 'Trenutno isključeno: dugme ostaje sivo dok AI prevod ne postane dostupan. Otvara dijalog „AI translate“ za svaki ključ paketa koji ima izvornu vrednost, ili samo za označene redove. Opis paketa iz podešavanja šalje se AI-ju kao kontekst. Posao popunjava prazne ćelije kao mašinski prevod i nikad ne prepisuje tekst u statusu draft ili reviewed.',
        },
      },
    },
    {
      selector: '[data-help="keys-qa"]',
      title: 'Check the package',
      body: 'Opens the QA dialog for the whole package, or only for the ticked rows, one locale at a time. It reads what is stored and writes nothing.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Paket prüfen',
          body: 'Öffnet den QA-Dialog für das ganze Paket oder nur für die angehakten Zeilen, jeweils für eine Sprache. Er liest das Gespeicherte und schreibt nichts.',
        },
        'sr-Latn-RS': {
          title: 'Provera paketa',
          body: 'Otvara QA dijalog za ceo paket ili samo za označene redove, za jedan jezik odjednom. Čita ono što je sačuvano i ništa ne upisuje.',
        },
      },
    },
    {
      selector: '[data-help="keys-search"]',
      title: 'Search keys and values',
      body: 'Finds keys whose name or value contains the text. The search is part of the address, so a filtered view is a link you can send.',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Schlüssel und Werte durchsuchen',
          body: 'Findet Schlüssel, deren Name oder Wert den Text enthält. Die Suche ist Teil der Adresse, eine gefilterte Ansicht ist also ein Link, den Sie weitergeben können.',
        },
        'sr-Latn-RS': {
          title: 'Pretraga ključeva i vrednosti',
          body: 'Pronalazi ključeve čije ime ili vrednost sadrži tekst. Pretraga je deo adrese, pa je filtriran prikaz link koji možete poslati.',
        },
      },
    },
    {
      selector: '[data-help="keys-missing-in"]',
      title: 'Show what is missing in a locale',
      body: 'Keeps only the keys with no value in the chosen locale: the work list for a translator of the package. Type to find a locale; clear the field to see every key again.',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Fehlendes in einer Sprache zeigen',
          body: 'Lässt nur die Schlüssel ohne Wert in der gewählten Sprache übrig: die Arbeitsliste für Übersetzende des Pakets. Tippen Sie, um eine Sprache zu finden; leeren Sie das Feld, um wieder alle Schlüssel zu sehen.',
        },
        'sr-Latn-RS': {
          title: 'Šta nedostaje na jeziku',
          body: 'Ostavlja samo ključeve bez vrednosti na izabranom jeziku: radna lista za prevodioca paketa. Kucajte da pronađete jezik; ispraznite polje da ponovo vidite sve ključeve.',
        },
      },
    },
    {
      selector: '[data-help="keys-status"]',
      title: 'Filter by status',
      body: 'Empty, machine, draft or reviewed, for one locale; a second field appears to choose which one.',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Nach Status filtern',
          body: 'Empty, machine, draft oder reviewed, jeweils für eine Sprache; ein zweites Feld erscheint, um sie zu wählen.',
        },
        'sr-Latn-RS': {
          title: 'Filtriranje po statusu',
          body: 'Empty, machine, draft ili reviewed, za jedan jezik; pojavljuje se drugo polje za izbor jezika.',
        },
      },
    },
    {
      selector: '[data-help="keys-locales"]',
      title: 'Choose the locale columns',
      body: 'Up to six of the package’s locales next to the source column. The choice is kept in this browser and shared with the project editor. Type to find a locale; the × on a locale removes its column.',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Sprachspalten wählen',
          body: 'Bis zu sechs Sprachen des Pakets neben der Quellspalte. Die Auswahl bleibt in diesem Browser gespeichert und gilt auch im Projekt-Editor. Tippen Sie, um eine Sprache zu finden; das × an einer Sprache entfernt ihre Spalte.',
        },
        'sr-Latn-RS': {
          title: 'Izbor kolona jezika',
          body: 'Do šest jezika paketa pored izvorne kolone. Izbor se pamti u ovom pregledaču i važi i u editoru projekta. Kucajte da pronađete jezik; × na jeziku uklanja njegovu kolonu.',
        },
      },
    },
    {
      selector: '[data-help="keys-cell"]',
      title: 'Edit a value',
      body: 'Click into a cell and type; Enter or leaving the cell saves it, Shift+Enter adds a line break and Escape drops the edit. The value is written to the package itself, not proposed. Cells are read-only unless you are an owner or translator of the package or an administrator.',
      section: 'Editing',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Einen Wert bearbeiten',
          body: 'In eine Zelle klicken und tippen; Enter oder das Verlassen der Zelle speichert, Umschalt+Enter fügt einen Zeilenumbruch ein und Escape verwirft die Änderung. Der Wert wird direkt ins Paket geschrieben, nicht vorgeschlagen. Die Zellen sind schreibgeschützt, außer Sie sind Owner oder Translator des Pakets oder Administrator.',
        },
        'sr-Latn-RS': {
          title: 'Izmena vrednosti',
          body: 'Kliknite u ćeliju i kucajte; Enter ili izlazak iz ćelije je čuva, Shift+Enter dodaje novi red, a Escape odbacuje izmenu. Vrednost se upisuje direktno u paket, ne kao predlog. Ćelije su samo za čitanje osim ako ste owner ili translator paketa ili administrator.',
        },
      },
    },
    {
      selector: '[data-help="keys-row-select"]',
      title: 'Tick rows',
      body: 'Ticked rows get the selection bar: AI translate, mark reviewed, add a tag, run QA or delete. The checkbox in the header ticks every loaded row.',
      section: 'Working on a selection',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Zeilen anhaken',
          body: 'Angehakte Zeilen erhalten die Auswahlleiste: KI-Übersetzung, als reviewed markieren, Tag hinzufügen, QA ausführen oder löschen. Das Kästchen im Kopf hakt alle geladenen Zeilen an.',
        },
        'sr-Latn-RS': {
          title: 'Označavanje redova',
          body: 'Označeni redovi dobijaju traku izbora: AI prevod, označavanje kao reviewed, dodavanje taga, QA ili brisanje. Polje u zaglavlju označava sve učitane redove.',
        },
      },
    },
    {
      selector: '[data-help="keys-bulk-delete"]',
      title: 'Delete package keys',
      body: 'Removes the ticked keys from the package and, at their next publish, from the bundle of every subscribing project. Owners and developers of the package and administrators may delete.',
      section: 'Working on a selection',
      context: 'selection',
      i18n: {
        'de-AT': {
          title: 'Paketschlüssel löschen',
          body: 'Entfernt die angehakten Schlüssel aus dem Paket und mit der nächsten Veröffentlichung aus dem Bundle jedes abonnierenden Projekts. Löschen dürfen Owner und Developer des Pakets sowie Administratoren.',
        },
        'sr-Latn-RS': {
          title: 'Brisanje ključeva paketa',
          body: 'Uklanja označene ključeve iz paketa, a pri sledećem objavljivanju i iz paketa datoteka svakog projekta pretplatnika. Brisati mogu owner i developer paketa i administratori.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Paketschlüssel',
      intro:
        'Der Editor für ein Paket: die Schlüssel, die ihm gehören, die Ausgangssprache und bis zu sechs Sprachspalten, mit Bearbeitung direkt in der Tabelle, die laufend speichert. Jedes abonnierende Projekt exportiert diese Schlüssel wie seine eigenen; ein hier geschriebener Wert erreicht also alle ihre Bundles bei der nächsten Veröffentlichung.',
      sections: {
        'Getting around': 'Überblick',
        'Finding a key': 'Einen Schlüssel finden',
        Editing: 'Bearbeiten',
        'Working on a selection': 'Mit einer Auswahl arbeiten',
      },
      contexts: {
        selection: 'Haken Sie das Kästchen am Anfang einer oder mehrerer Zeilen an.',
      },
      tips: [
        'Hier wächst ein Paket: Ein neuer Schlüssel braucht eines der Präfixe des Pakets, und die Schlüsselseite eines Projekts lehnt dieses Präfix ab, weil es dem Paket gehört.',
        'Hier zählt Ihre Rolle im Paket, nicht in einem Projekt. Owner und Translator des Pakets bearbeiten Werte direkt; Owner und Developer legen Schlüssel an, benennen sie um, taggen und löschen sie; Administratoren dürfen alles. Dieselbe Änderung auf der Schlüsselseite eines Projekts wird zum Vorschlag, außer Sie sind Owner des Pakets.',
        'Ein Paket hat keine Schaltfläche „Export“: Seine Schlüssel gelangen in die Bundles der abonnierenden Projekte oder über Releases in die eigenen Dateien des Pakets.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Ključevi paketa',
      intro:
        'Editor za jedan paket: ključevi koje poseduje, izvorni jezik i do šest kolona jezika, uz izmenu direktno u tabeli koja se čuva usput. Svaki projekat pretplatnik izvozi ove ključeve kao svoje, pa vrednost upisana ovde stiže u sve njihove datoteke pri sledećem objavljivanju.',
      sections: {
        'Getting around': 'Snalaženje',
        'Finding a key': 'Pronalaženje ključa',
        Editing: 'Izmena',
        'Working on a selection': 'Rad sa izborom',
      },
      contexts: {
        selection: 'Označite polje na početku jednog ili više redova.',
      },
      tips: [
        'Ovde paket raste: novi ključ mora imati jedan od prefiksa paketa, a stranica Keys projekta odbija taj prefiks jer ga poseduje paket.',
        'Ovde odlučuje vaša uloga na paketu, ne u nekom projektu. Owner i translator paketa menjaju vrednosti direktno; owner i developer kreiraju, preimenuju, taguju i brišu ključeve; administratori mogu sve. Ista izmena sa stranice Keys projekta postaje predlog, osim ako ste owner paketa.',
        'Paket nema dugme „Export“: njegovi ključevi stižu u datoteke projekata pretplatnika, ili kao sopstvene datoteke paketa preko Releases.',
      ],
    },
  },
};
